import React, { useState, useEffect, useCallback, useRef } from 'react'
import { X, Upload, FileText, Loader2, AlertCircle, Scan } from 'lucide-react'
import useOCR from '../hooks/useOCR'
import { useBlobUrlFromApi } from '../hooks/useBlobUrl'
import { MAX_UPLOAD_FILE_SIZE_BYTES, UPLOAD_FILE_SIZE_ERROR_MESSAGE } from '../constants/upload'

const TAB_UPLOAD = 'upload'
const TAB_EXISTING = 'existing'

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif']
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff']

/** Thumbnail for one image in the existing-images grid; loads via getImagePreview. */
const ExistingImageThumbnail = ({ image, objectId, getImagePreview, isSelected, onSelect }) => {
  const fetchImage = useCallback(() => {
    if (!getImagePreview || !objectId || !image) return null
    return getImagePreview(objectId, image)
  }, [objectId, image, getImagePreview])
  const { blobUrl, isLoading } = useBlobUrlFromApi(
    fetchImage,
    [objectId, image?.objectImageId, image?.objectImageEditId ?? image?.maxObjectImageEditId],
    'image/jpeg'
  )
  return (
    <button
      type="button"
      onClick={() => onSelect(image)}
      className={`rounded border-2 p-0.5 aspect-square flex items-center justify-center overflow-hidden bg-gray-100 ${isSelected ? 'border-indigo-600 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'}`}
    >
      {isLoading && !blobUrl && <Loader2 size={20} className="animate-spin text-gray-400" />}
      {blobUrl && <img src={blobUrl} alt="" className="w-full h-full object-contain" />}
      {!isLoading && !blobUrl && <FileText size={24} className="text-gray-400" />}
    </button>
  )
}

/**
 * OCRModal - Modal for OCR input: upload new image or select existing image, run OCR, auto-apply to form.
 * Same flow as voice recognition: auto-apply on success, feedback only on error.
 */
const OCRModal = ({
  isOpen,
  onClose,
  satsueiMemoCategoryId,
  onApplyData,
  objectId,
  imageList = [],
  onUploadImage,
  onApiError,
  getImagePreview
}) => {
  const [activeTab, setActiveTab] = useState(TAB_UPLOAD)
  const [selectedExistingImage, setSelectedExistingImage] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null)

  const { isProcessing, rawText, error: ocrError, recognizeImage, clearResults } = useOCR()

  const clearFile = useCallback(() => {
    setSelectedFile(null)
    setUploadError(null)
  }, [])

  const selectFile = useCallback((file) => {
    setUploadError(null)
    if (!file) {
      setSelectedFile(null)
      return true
    }
    const name = file.name || ''
    const ext = name.lastIndexOf('.') >= 0 ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : ''
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setUploadError(`対応形式は ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()} です`)
      return false
    }
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      setUploadError(UPLOAD_FILE_SIZE_ERROR_MESSAGE)
      return false
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
      setUploadError(`対応形式は ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()} です`)
      return false
    }
    setSelectedFile(file)
    return true
  }, [])

  const handleSelectExistingImage = useCallback((img) => {
    setSelectedExistingImage(prev => prev?.objectImageId === img.objectImageId ? null : img)
  }, [])

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(TAB_UPLOAD)
      setSelectedExistingImage(null)
      setErrorMessage(null)
      clearResults()
      clearFile()
    }
  }, [isOpen, clearResults, clearFile])

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0]
    if (file) selectFile(file)
    e.target.value = ''
  }, [selectFile])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const file = e.dataTransfer?.files?.[0]
    if (file) selectFile(file)
  }, [selectFile])

  const handleDragOver = useCallback((e) => e.preventDefault(), [])

  const buildImageParams = useCallback((img) => ({
    objectId: Number(objectId),
    objectImageId: img.objectImageId,
    objectImageEditId: img.objectImageEditId ?? img.maxObjectImageEditId ?? null,
    filenameExtension: img.filenameExtension || 'jpg',
    isUploaded: img.isUploaded ?? 1,
    type: 'satsueiImage',
    satsueiMemoCategoryId
  }), [objectId, satsueiMemoCategoryId])

  const handleUploadAndOCR = useCallback(async () => {
    if (!selectedFile || !onUploadImage || !objectId) return
    setErrorMessage(null)
    try {
      const imageRecord = await onUploadImage(selectedFile)
      if (!imageRecord || imageRecord.objectImageId == null) {
        setErrorMessage('アップロード後の画像情報を取得できませんでした')
        return
      }
      const params = buildImageParams(imageRecord)
      const result = await recognizeImage(params)
      if (result.extractedData && Object.keys(result.extractedData).length > 0) {
        onApplyData(result.extractedData)
        onClose()
      } else {
        setErrorMessage('データを抽出できませんでした。もう一度お試しください。')
      }
    } catch (err) {
      console.error('OCR failed:', err)
      if (onApiError) onApiError(err, 'OCRに失敗しました')
      setErrorMessage(err.message || err.response?.data?.message || 'OCRに失敗しました。もう一度お試しください。')
    }
  }, [selectedFile, onUploadImage, objectId, buildImageParams, recognizeImage, onApplyData, onClose, onApiError])

  const handleExistingOCR = useCallback(async () => {
    if (!selectedExistingImage || !objectId) return
    setErrorMessage(null)
    try {
      const params = buildImageParams(selectedExistingImage)
      const result = await recognizeImage(params)
      if (result.extractedData && Object.keys(result.extractedData).length > 0) {
        onApplyData(result.extractedData)
        onClose()
      } else {
        setErrorMessage('データを抽出できませんでした。もう一度お試しください。')
      }
    } catch (err) {
      console.error('OCR failed:', err)
      if (onApiError) onApiError(err, 'OCRに失敗しました')
      setErrorMessage(err.message || err.response?.data?.message || 'OCRに失敗しました。もう一度お試しください。')
    }
  }, [selectedExistingImage, objectId, buildImageParams, recognizeImage, onApplyData, onClose, onApiError])

  if (!isOpen) return null

  const displayError = errorMessage || ocrError || uploadError

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">OCR入力</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 transition-colors" aria-label="閉じる">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100 flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => { setActiveTab(TAB_UPLOAD); setSelectedExistingImage(null); setErrorMessage(null); clearFile(); clearResults(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === TAB_UPLOAD ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            アップロード
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab(TAB_EXISTING); setSelectedExistingImage(null); setErrorMessage(null); clearFile(); clearResults(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === TAB_EXISTING ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            既存画像
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {displayError && !isProcessing && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{displayError}</span>
            </div>
          )}

          {isProcessing && (
            <div className="text-center py-8">
              <Loader2 size={48} className="mx-auto mb-4 text-indigo-600 animate-spin" />
              <p className="text-gray-600">OCR処理中...</p>
            </div>
          )}

          {!isProcessing && activeTab === TAB_UPLOAD && (
            <div className="text-center">
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-4 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Upload size={40} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 text-sm">クリックまたはドラッグで画像を選択（JPG, PNG, GIF, BMP, TIFF / 10MB以下）</p>
              </div>
              {selectedFile && (
                <div className="mb-4 text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-indigo-800">
                    <FileText size={18} className="flex-shrink-0" />
                    <span className="text-sm font-medium truncate max-w-[240px]" title={selectedFile.name}>
                      選択したファイル: {selectedFile.name}
                    </span>
                  </div>
                </div>
              )}
              {selectedFile && (
                <button
                  type="button"
                  onClick={handleUploadAndOCR}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors mx-auto"
                >
                  <Scan size={20} />
                  アップロードしてOCR実行
                </button>
              )}
            </div>
          )}

          {!isProcessing && activeTab === TAB_EXISTING && (
            <div>
              {(!imageList || imageList.length === 0) ? (
                <p className="text-gray-500 text-center py-6">画像がありません。先に画像をアップロードしてください。</p>
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-2 mb-4 max-h-64 overflow-y-auto">
                    {imageList.map((img) => (
                      <ExistingImageThumbnail
                        key={img.objectImageId}
                        image={img}
                        objectId={objectId}
                        getImagePreview={getImagePreview}
                        isSelected={selectedExistingImage?.objectImageId === img.objectImageId}
                        onSelect={handleSelectExistingImage}
                      />
                    ))}
                  </div>
                  {selectedExistingImage && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleExistingOCR}
                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        <Scan size={20} />
                        OCR実行
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!isProcessing && displayError && rawText && (
            <div className="mt-4 text-left">
              <h3 className="text-sm font-medium text-gray-700 mb-2">認識されたテキスト</h3>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-900 text-sm whitespace-pre-wrap">{rawText}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OCRModal
